import { Component, EventEmitter, inject, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { combineLatest, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { parse, ParseResult, unparse } from 'papaparse';
import { TranslateService } from '@ngx-translate/core';
import { MigrationsCiscoSourceFieldMappingContext } from './migrations-cisco-source-field-mapping.context';
import { MigrationsCiscoSiteManagementContext } from '../migrations-cisco-site-management/migrations-cisco-site-management.context';
import {
  CiscoMigrationSite,
  CiscoMigrationSiteSourceField,
  CiscoMigrationSiteSourceValues,
  CiscoMigrationSiteValueMapping,
} from '../../../shared/models/generated/ziroModels';
import {
  DatatableColumn,
  DatatableOnRowFieldChange,
  DatatableRow,
} from '../../../shared/tables/datatable/datatable.models';
import { DatatableComponent } from '../../../shared/tables/datatable/datatable.component';
import { DatatableFieldTypes } from '../../../shared/tables/datatable/datatable-field.models';
import { FilterSelectOption } from '../../../shared/tables/datatable/filter-select/filter-select.component';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ButtonSizes, ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { ToastService } from '../../../shared/services/toast.service';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import { ZiroFormsValidationState } from '../../../forms/ziro-forms-models';

interface MappingRow extends DatatableRow {
  rowId: string;
  sourceValue: string;
  sourceValueSubtext?: string;
  count: number;
  siteId: string;
}

interface MappingCsvRow {
  [CSV_RAW_VALUE_HEADER]?: string;
  [CSV_SITE_NAME_HEADER]?: string;
}

const CSV_RAW_VALUE_HEADER = 'Raw value';
const CSV_SITE_NAME_HEADER = 'Site name';
const CSV_HEADERS: string[] = [CSV_RAW_VALUE_HEADER, CSV_SITE_NAME_HEADER];

interface SourceFieldCard {
  value: CiscoMigrationSiteSourceField;
  labelKey: string;
  descriptionKey: string;
  discoveredCount: number;
}

interface RowImportIssue {
  flaggedValue: string | null;
  isValid: ZiroFormsValidationState.INVALID | ZiroFormsValidationState.WARNING;
  message: string;
}

export interface SourceFieldMappingBottomNavState {
  saveDisabled: boolean;
  saveDisabledTooltipKey: string;
  savePending: boolean;
  cancelDisabled: boolean;
  isDirty: boolean;
}

const SOURCE_VALUE_BUCKET_BY_FIELD: Record<CiscoMigrationSiteSourceField, keyof CiscoMigrationSiteSourceValues> = {
  [CiscoMigrationSiteSourceField.DEVICE_POOL]: 'devicePool',
  [CiscoMigrationSiteSourceField.ROUTE_PARTITION]: 'routePartition',
  [CiscoMigrationSiteSourceField.DEVICE_CALLING_SEARCH_SPACE]: 'deviceCallingSearchSpace',
  [CiscoMigrationSiteSourceField.DN_CALLING_SEARCH_SPACE]: 'dnCallingSearchSpace',
};

@Component({
  selector: 'ziro-migrations-cisco-source-field-mapping',
  templateUrl: './migrations-cisco-source-field-mapping.component.html',
  standalone: false,
  styleUrls: ['./migrations-cisco-source-field-mapping.component.scss'],
  providers: [MigrationsCiscoSourceFieldMappingContext],
})
export class MigrationsCiscoSourceFieldMappingComponent implements OnInit, OnDestroy {
  private _context = inject(MigrationsCiscoSourceFieldMappingContext);
  private _sitesContext = inject(MigrationsCiscoSiteManagementContext);
  private _toastService = inject(ToastService);
  private _ziroModalService = inject(ZiroModalService);
  private _translateService = inject(TranslateService);
  private _domSanitizer = inject(DomSanitizer);

  @ViewChild(DatatableComponent) private _datatable: DatatableComponent<MappingRow>;

  @Output() readonly bottomNavStateChange = new EventEmitter<SourceFieldMappingBottomNavState>();

  isLoading = true;
  isSavingSourceField = false;
  isSavingMappings = false;
  isMappingsDirty = false;
  hasImportDuplicates = false;
  templateUrl: string;

  persistedSourceField: CiscoMigrationSiteSourceField | null = null;
  pendingSourceField: CiscoMigrationSiteSourceField | null = null;
  sourceFieldCards: SourceFieldCard[] = [];

  rows: MappingRow[] = [];
  sites: CiscoMigrationSite[] = [];
  siteOptions: FilterSelectOption[] = [];

  get isPreviewingUnsavedField(): boolean {
    return !!this.pendingSourceField && this.pendingSourceField !== this.persistedSourceField;
  }

  get importDisabledTooltipKey(): string {
    if (this.isPreviewingUnsavedField) {
      return 'tkey;migrations.cisco.site_management.source_field_mapping.import.disabled_tooltip.unsaved_field';
    }
    if (this.isSavingMappings) {
      return 'tkey;migrations.cisco.site_management.source_field_mapping.import.disabled_tooltip.saving';
    }
    return '';
  }

  readonly buttonSizes = ButtonSizes;
  readonly buttonStyles = ButtonStyles;
  readonly buttonTypes = ButtonTypes;
  readonly icons = ZiroIcons;

  columns: DatatableColumn<MappingRow>[] = [
    {
      name: 'sourceValue',
      label: 'tkey;migrations.cisco.site_management.source_field_mapping.table.raw_value.header',
      columnSize: 'col-sm-4',
      columnCharacterWidth: 30,
    },
    {
      name: 'count',
      label: 'tkey;migrations.cisco.site_management.source_field_mapping.table.dn_count.header',
      columnSize: 'col-sm-2',
      columnCharacterWidth: 10,
    },
    {
      name: 'siteId',
      label: 'tkey;migrations.cisco.site_management.source_field_mapping.table.site.header',
      columnSize: 'col-sm-6',
      columnCharacterWidth: 30,
      sortFn: (a: string | null, b: string | null) => (this._siteName(a) ?? '').localeCompare(this._siteName(b) ?? ''),
    },
  ];

  private _sourceValues: CiscoMigrationSiteSourceValues | null = null;
  private _mappings: CiscoMigrationSiteValueMapping[] = [];
  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this.templateUrl = this._getTemplateUrl() as string;

    const stateSub = combineLatest([
      this._context.sourceField$,
      this._context.sourceValues$,
      this._context.mappings$,
      this._sitesContext.state$,
    ]).subscribe(([sourceField, sourceValues, mappings, sites]) => {
      this.persistedSourceField = sourceField;
      this.pendingSourceField = sourceField;
      this._sourceValues = sourceValues;
      this._mappings = mappings ?? [];
      this.sites = sites ?? [];
      this.siteOptions = this.sites.map((site) => ({ value: site.siteId, label: site.name }));
      this._buildSourceFieldCards();
      this._buildRows();
      this.isMappingsDirty = false;
      this.hasImportDuplicates = false;
      this.isLoading = false;
      this._emitBottomNavState();
    });
    this._subscriptions.add(stateSub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  onSourceFieldCardSelected(sourceField: CiscoMigrationSiteSourceField): void {
    if (this.isSavingSourceField) {
      return;
    }
    this.pendingSourceField = sourceField;
    this._buildRows();
    this.isMappingsDirty = false;
    this.hasImportDuplicates = false;
    this._emitBottomNavState();
  }

  onSaveSourceField(): void {
    if (!this.pendingSourceField || this.pendingSourceField === this.persistedSourceField || this.isSavingSourceField) {
      return;
    }

    if (this._mappings.length > 0) {
      this._promptSourceFieldChangeConfirmation();
    } else {
      this._applySourceFieldChange(this.pendingSourceField);
    }
  }

  onRowFieldChange(event: DatatableOnRowFieldChange<MappingRow>): void {
    if (event.columnId === 'siteId') {
      event.row.siteId = event.newValue || null;
      event.row.sourceValueSubtext = undefined;
    }
    this.isMappingsDirty = true;
    this._recomputeImportDuplicatesFlag();
    this._emitBottomNavState();
  }

  onSaveMappings(): void {
    if (!this.pendingSourceField || this.isPreviewingUnsavedField) {
      return;
    }

    const hasErrors = this.rows.some((row) => this._datatable?.isErrorPresentOnRow(row));
    if (hasErrors || this.hasImportDuplicates) {
      return;
    }

    const mappings: CiscoMigrationSiteValueMapping[] = this.rows
      .filter((row) => !!row.siteId)
      .map((row) => ({ siteId: row.siteId as string, sourceValue: row.sourceValue }));

    this.isSavingMappings = true;
    this._emitBottomNavState();
    this._context
      .saveMappings(mappings)
      .pipe(
        tap(() => {
          this.isSavingMappings = false;
          this.isMappingsDirty = false;
          this._toastService.push(
            ToastTypes.SUCCESS,
            ZiroIcons.SAVE_SUCCESS,
            'tkey;shared.toast.save.success.title',
            'tkey;migrations.cisco.site_management.source_field_mapping.toast.save_mappings.message',
          );
          this._emitBottomNavState();
        }),
      )
      .subscribe({
        error: () => {
          this.isSavingMappings = false;
          this._emitBottomNavState();
        },
      });
  }

  onImportFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.item(0);
    input.value = '';

    if (!this.pendingSourceField || this.isPreviewingUnsavedField) {
      return;
    }

    if (file) {
      this._importCsv(file);
    }
  }

  private _getTemplateUrl(): SafeUrl {
    const csvData = unparse({ fields: CSV_HEADERS, data: [] });
    return this._domSanitizer.bypassSecurityTrustUrl(`data:text/plain;charset=utf8,${encodeURIComponent(csvData)}`);
  }

  private _buildSourceFieldCards(): void {
    this.sourceFieldCards = (Object.values(CiscoMigrationSiteSourceField) as CiscoMigrationSiteSourceField[]).map(
      (field) => ({
        value: field,
        labelKey: this._labelKeyFor(field),
        descriptionKey: this._descriptionKeyFor(field),
        discoveredCount: (this._sourceValues?.[SOURCE_VALUE_BUCKET_BY_FIELD[field]] ?? []).length,
      }),
    );
  }

  private _labelKeyFor(field: CiscoMigrationSiteSourceField): string {
    return `tkey;migrations.cisco.site_management.source_field_mapping.source_field.options.${field.toLowerCase()}.label`;
  }

  private _descriptionKeyFor(field: CiscoMigrationSiteSourceField): string {
    return `tkey;migrations.cisco.site_management.source_field_mapping.source_field.options.${field.toLowerCase()}.description`;
  }

  private _buildRows(): void {
    if (!this.pendingSourceField || !this._sourceValues) {
      this.rows = [];
      return;
    }

    const bucket = this._sourceValues[SOURCE_VALUE_BUCKET_BY_FIELD[this.pendingSourceField]] ?? [];
    const isPreviewingSavedField = this.pendingSourceField === this.persistedSourceField;
    const mappingBySourceValue = new Map(
      isPreviewingSavedField ? this._mappings.map((mapping) => [mapping.sourceValue, mapping.siteId]) : [],
    );

    this.rows = bucket.map((value) => {
      const mappedSiteId = mappingBySourceValue.get(value.sourceValue) ?? null;
      const siteId = this.sites.some((site) => site.siteId === mappedSiteId) ? mappedSiteId : null;
      return this._toRow(value.sourceValue, value.count, siteId);
    });
  }

  private _toRow(
    sourceValue: string,
    count: number,
    siteId: string | null,
    siteIssue?: RowImportIssue,
    duplicateMessage?: string,
  ): MappingRow {
    return {
      rowId: sourceValue,
      sourceValue,
      sourceValueSubtext: duplicateMessage,
      count,
      siteId,
      hardcodedTooltips: {
        sourceValue: sourceValue.length > 25 ? sourceValue : '',
      },
      fields: {
        siteId: {
          type: DatatableFieldTypes.SELECT,
          value: siteId,
          isClearable: true,
          options: this.siteOptions,
          bindValue: 'value',
          bindLabel: 'label',
          validation: siteIssue
            ? [
                {
                  validator: (value: string | null): ZiroFormsValidationState =>
                    value === siteIssue.flaggedValue ? siteIssue.isValid : ZiroFormsValidationState.VALID,
                  message: (): string => siteIssue.message,
                },
              ]
            : undefined,
        },
      },
    };
  }

  private _siteName(siteId: string | null): string | null {
    return this.sites.find((site) => site.siteId === siteId)?.name ?? null;
  }

  matchesSiteNameFilter = (searchFilter: string, row: MappingRow): boolean =>
    (this._siteName(row.siteId) ?? '').toLowerCase().includes(searchFilter.toLowerCase().trim());

  private _promptSourceFieldChangeConfirmation(): void {
    const newSourceField = this.pendingSourceField as CiscoMigrationSiteSourceField;
    const currentLabel = this.persistedSourceField
      ? this._translateService.instant(this._labelKeyFor(this.persistedSourceField))
      : '';
    const newLabel = this._translateService.instant(this._labelKeyFor(newSourceField));

    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.EXCLAMATION,
        iconClass: 'text-danger',
        title: this._translateService.instant(
          'tkey;migrations.cisco.site_management.source_field_mapping.change_source_field_modal.title',
        ),
        promptBody: this._translateService.instant(
          'tkey;migrations.cisco.site_management.source_field_mapping.change_source_field_modal.body',
          {
            currentField: currentLabel,
            newField: newLabel,
            count: this._mappings.length,
          },
        ),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;migrations.cisco.site_management.source_field_mapping.change_source_field_modal.keep_button',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'source-field-change-modal-keep-button',
          },
          {
            label:
              'tkey;migrations.cisco.site_management.source_field_mapping.change_source_field_modal.confirm_button',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'source-field-change-modal-confirm-button',
            cb: () => this._context.saveSourceField(newSourceField),
          },
        ],
      },
    };

    this._ziroModalService
      .openPromptModal(() => options.modalViewProperties, options)
      .subscribe((result) => {
        if (result === false) {
          this.pendingSourceField = this.persistedSourceField;
          this._buildRows();
          this._emitBottomNavState();
          return;
        }
        this._toastService.push(
          ToastTypes.SUCCESS,
          ZiroIcons.SAVE_SUCCESS,
          'tkey;shared.toast.save.success.title',
          'tkey;migrations.cisco.site_management.source_field_mapping.toast.save_source_field.message',
        );
      });
  }

  private _applySourceFieldChange(sourceField: CiscoMigrationSiteSourceField): void {
    this.isSavingSourceField = true;
    this._context
      .saveSourceField(sourceField)
      .pipe(
        tap(() => {
          this.isSavingSourceField = false;
          this._toastService.push(
            ToastTypes.SUCCESS,
            ZiroIcons.SAVE_SUCCESS,
            'tkey;shared.toast.save.success.title',
            'tkey;migrations.cisco.site_management.source_field_mapping.toast.save_source_field.message',
          );
        }),
      )
      .subscribe({
        error: () => {
          this.isSavingSourceField = false;
        },
      });
  }

  private _recomputeImportDuplicatesFlag(): void {
    this.hasImportDuplicates = this.rows.some((row) => !!row.sourceValueSubtext);
  }

  getBottomNavState(): SourceFieldMappingBottomNavState {
    return {
      saveDisabled:
        this.isSavingMappings || !this.isMappingsDirty || this.hasImportDuplicates || this.isPreviewingUnsavedField,
      saveDisabledTooltipKey: this._getSaveMappingsDisabledTooltipKey(),
      savePending: this.isSavingMappings,
      cancelDisabled: this.isSavingMappings,
      isDirty: this.isMappingsDirty,
    };
  }

  private _getSaveMappingsDisabledTooltipKey(): string {
    if (this.hasImportDuplicates) {
      return 'tkey;migrations.cisco.site_management.source_field_mapping.save_mappings.disabled_tooltip.duplicates';
    }
    if (this.isPreviewingUnsavedField) {
      return 'tkey;migrations.cisco.site_management.source_field_mapping.save_mappings.disabled_tooltip.unsaved_field';
    }
    return '';
  }

  private _emitBottomNavState(): void {
    this.bottomNavStateChange.emit(this.getBottomNavState());
  }

  private _importCsv(file: File): void {
    if (!file) {
      return;
    }

    parse<MappingCsvRow>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result: ParseResult<MappingCsvRow>) => {
        const headers = result.meta.fields ?? [];

        if (!result.data.length) {
          this._pushImportErrorToast(
            'tkey;migrations.cisco.site_management.source_field_mapping.csv_upload.error.empty_file',
          );
          return;
        }
        if (!headers.includes(CSV_RAW_VALUE_HEADER)) {
          this._pushImportErrorToast(
            'tkey;migrations.cisco.site_management.source_field_mapping.csv_upload.error.missing_raw_value_column',
          );
          return;
        }
        if (result.data.some((row) => !(row[CSV_RAW_VALUE_HEADER] ?? '').trim())) {
          this._pushInvalidCsvToast();
          return;
        }

        this._stageImportedRows(result.data);
      },
      error: () => {
        this._pushImportErrorToast(
          'tkey;migrations.cisco.site_management.source_field_mapping.csv_upload.error.parse_error',
        );
      },
    });
  }

  private _pushImportErrorToast(messageKey: string): void {
    this._toastService.push(
      ToastTypes.ERROR,
      ZiroIcons.EXCLAMATION,
      'tkey;migrations.cisco.site_management.source_field_mapping.csv_upload.toast.error.title',
      this._translateService.instant(messageKey),
    );
  }

  private _pushInvalidCsvToast(): void {
    this._toastService.push(
      ToastTypes.ERROR,
      ZiroIcons.EXCLAMATION,
      'tkey;migrations.cisco.site_management.source_field_mapping.invalid_csv.toast.title',
      this._translateService.instant(
        'tkey;migrations.cisco.site_management.source_field_mapping.invalid_csv.toast.message',
      ),
    );
  }

  private _stageImportedRows(csvRows: MappingCsvRow[]): void {
    const rowsBySourceValue = new Map(this.rows.map((row) => [row.sourceValue, row]));
    const sourceValueOccurrences = new Map<string, number>();
    csvRows.forEach((csvRow) => {
      const rawValue = (csvRow[CSV_RAW_VALUE_HEADER] ?? '').trim();
      if (rawValue) {
        sourceValueOccurrences.set(rawValue, (sourceValueOccurrences.get(rawValue) ?? 0) + 1);
      }
    });

    const outcomeByRawValue = new Map<string, { row: MappingRow; isError: boolean }>();
    let unmatchedCsvRowCount = 0;

    csvRows.forEach((csvRow) => {
      const rawValue = (csvRow[CSV_RAW_VALUE_HEADER] ?? '').trim();
      const siteName = (csvRow[CSV_SITE_NAME_HEADER] ?? '').trim();
      const row = rowsBySourceValue.get(rawValue);

      if (!rawValue || !row) {
        unmatchedCsvRowCount++;
        return;
      }

      const isDuplicate = (sourceValueOccurrences.get(rawValue) ?? 0) > 1;

      if (isDuplicate) {
        outcomeByRawValue.set(rawValue, {
          row: this._toRow(
            row.sourceValue,
            row.count,
            row.siteId,
            undefined,
            this._translateService.instant(
              'tkey;migrations.cisco.site_management.source_field_mapping.csv_upload.error.duplicate_raw_value',
            ),
          ),
          isError: true,
        });
        return;
      }

      if (!siteName) {
        outcomeByRawValue.set(rawValue, { row: this._toRow(row.sourceValue, row.count, null), isError: false });
        return;
      }

      const matchedSite = this.sites.find((site) => site.name.trim().toLowerCase() === siteName.toLowerCase());

      if (!matchedSite) {
        outcomeByRawValue.set(rawValue, {
          row: this._toRow(row.sourceValue, row.count, row.siteId, {
            flaggedValue: row.siteId,
            isValid: ZiroFormsValidationState.WARNING,
            message: this._translateService.instant(
              'tkey;migrations.cisco.site_management.source_field_mapping.csv_upload.error.unknown_site',
              { siteName },
            ),
          }),
          isError: true,
        });
        return;
      }

      outcomeByRawValue.set(rawValue, {
        row: this._toRow(row.sourceValue, row.count, matchedSite.siteId),
        isError: false,
      });
    });

    const outcomes = [...outcomeByRawValue.values()];
    const stagedCount = outcomes.filter((outcome) => !outcome.isError).length;
    const errorCount = outcomes.filter((outcome) => outcome.isError).length + unmatchedCsvRowCount;

    this.rows = this.rows.map((row) => outcomeByRawValue.get(row.sourceValue)?.row ?? row);
    this.isMappingsDirty = true;
    this._recomputeImportDuplicatesFlag();
    this._emitBottomNavState();

    this._toastService.push(
      errorCount > 0 ? ToastTypes.WARNING : ToastTypes.SUCCESS,
      errorCount > 0 ? ZiroIcons.EXCLAMATION : ZiroIcons.SAVE_SUCCESS,
      'tkey;migrations.cisco.site_management.source_field_mapping.csv_upload.toast.summary.title',
      this._translateService.instant(
        'tkey;migrations.cisco.site_management.source_field_mapping.csv_upload.toast.summary.message',
        { stagedCount, errorCount },
      ),
    );
  }
}
